// app/help/components/HelpNavigation.tsx
"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "utils/utils";
import {
  BookOpen,
  CreditCard,
  HelpCircle,
  Keyboard,
  MessageCircle,
  ScanLine,
  Search,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import CollectionIcon from "modules/common/components/Icons/CollectionIcon";
import DecksIcon from "modules/common/components/Icons/DecksIcon";

interface HelpSection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
}

export const helpSections: HelpSection[] = [
  {
    id: "overview",
    title: "Getting Started",
    description: "New to the platform? Start here",
    icon: <BookOpen className="h-5 w-5" />,
    href: "/help",
  },

  {
    id: "faq",
    title: "FAQ",
    description: "Frequently asked questions",
    icon: <HelpCircle className="h-5 w-5" />,
    href: "/help/faq",
  },
  {
    id: "scanner",
    title: "Card Scanner",
    description: "Scan a binder on your phone",
    icon: <ScanLine className="h-5 w-5" />,
    href: "/help/scanner",
  },
  {
    id: "collection",
    title: "Collection Management",
    description: "Add, import, filter, and sort cards",
    icon: <CollectionIcon className="h-5 w-5" />,
    href: "/help/collection",
  },
  {
    id: "trading",
    title: "Trading & Lists",
    description: "Haves, wants, and nearby matches",
    icon: <CreditCard className="h-5 w-5" />,
    href: "/help/trading",
  },
  {
    id: "decks",
    title: "Deck Building",
    description: "Build a deck and check what you own",
    icon: <DecksIcon className="h-5 w-5" />,
    href: "/help/decks",
  },
  {
    id: "search",
    title: "Search & Filters",
    description: "Finding cards and content",
    icon: <Search className="h-5 w-5" />,
    href: "/help/search",
  },
  {
    id: "stores",
    title: "Local Game Stores",
    description: "Pick shops and see nearby cards",
    icon: <Users className="h-5 w-5" />,
    href: "/help/stores",
  },
  {
    id: "account",
    title: "Account Settings",
    description: "Profile, stores, games, and pricing",
    icon: <Settings className="h-5 w-5" />,
    href: "/help/account",
  },
  {
    id: "shortcuts",
    title: "Keyboard Shortcuts",
    description: "Navigate faster with keyboard shortcuts",
    icon: <Keyboard className="h-5 w-5" />,
    href: "/help/shortcuts",
  },
  {
    id: "privacy",
    title: "Privacy & Security",
    description: "Data protection and safety",
    icon: <Shield className="h-5 w-5" />,
    href: "/help/privacy",
  },
  {
    id: "contact",
    title: "Contact Support",
    description: "Get help from our team",
    icon: <MessageCircle className="h-5 w-5" />,
    href: "/help/contact",
  },
];

export function HelpNavigation() {
  const pathname = usePathname();

  return (
    <nav className="rounded-box border-base-300 bg-base-300 md: z-10 border p-4">
      <h3 className="text-base-content mb-4 text-lg font-semibold">
        Help Topics
      </h3>

      <ul className="space-y-2">
        {helpSections.map((section) => {
          const isActive = pathname === section.href;

          return (
            <li key={section.id}>
              <Link
                href={section.href}
                className={cn(
                  "group flex items-start gap-3 rounded-lg p-3 transition-colors duration-200",
                  !isActive && "hover:bg-base-200",
                  isActive &&
                    "bg-primary text-primary-content hover:bg-primary/70"
                )}
              >
                <div
                  className={cn(
                    "mt-0.5 shrink-0",
                    isActive ? "text-primary-content" : "text-base-content/70"
                  )}
                >
                  {section.icon}
                </div>

                <div className="min-w-0 flex-1">
                  <h4
                    className={cn(
                      "text-sm font-medium leading-tight",
                      isActive ? "text-primary-content" : "text-base-content"
                    )}
                  >
                    {section.title}
                  </h4>
                  <p
                    className={cn(
                      "mt-1 text-xs leading-tight",
                      isActive
                        ? "text-primary-content/80"
                        : "text-base-content/60"
                    )}
                  >
                    {section.description}
                  </p>
                </div>
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Mobile Contact Card */}
      <div className="bg-base-100 rounded-box border-base-content/20 mt-6 border p-4">
        <h4 className="text-base-content mb-2 text-sm font-medium">
          Need more help?
        </h4>
        <p className="text-base-content/70 mb-3 text-xs">
          Our support team is here to assist you with any questions.
        </p>
        <Link href="/contact" className="btn btn-primary btn-sm w-auto">
          Contact Support
        </Link>
      </div>
    </nav>
  );
}
